import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    Tooltip,
    Button,
    CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { Link } from "react-router-dom";
import { authHeader } from "../auth";
import { excelSerialToDateString } from "../timeserver/uselivedata";

// Petit champ texte + bouton copier, réutilisé pour chaque colonne "document".
function CopyField({ value }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* ignore — le presse-papier peut être refusé par le navigateur */
        }
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <TextField
                value={value}
                size="small"
                variant="outlined"
                InputProps={{ readOnly: true, sx: { fontSize: "0.8rem" } }}
                sx={{ width: 150 }}
                onFocus={(e) => e.target.select()}
            />
            <Tooltip title={copied ? "Copié !" : "Copier"}>
                <IconButton size="small" onClick={handleCopy} color={copied ? "success" : "default"}>
                    {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Tooltip>
        </Box>
    );
}

// Suffixes des documents liés à la course, dérivés du codex.
const DOC_SUFFIXES = [
    "", // le codex lui-même
    "_participants",
    "_SL1",
    "_IORes1",
    "_ORes1",
    "_SL2",
    "_IORes2",
    "_ORes2",
];

// Archivage (réversible, table séparée live_meta) + suppression complète
// (irréversible, DELETE réel dans live_snapshots via live.js) ajoutés le
// 29.08.2026.
export default function LiveOverview() {
    const [lives, setLives] = useState([]);
    const [meta, setMeta] = useState({}); // codex -> { archivedAt }
    const [loading, setLoading] = useState(true);
    const [metaLoaded, setMetaLoaded] = useState(false);
    const [error, setError] = useState("");
    const [showArchived, setShowArchived] = useState(false);

    const apiFetch = useCallback(
        (url, options = {}) =>
            fetch(url, { ...options, headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) } }),
        []
    );

    useEffect(() => {
        fetch("/api/live", { headers: { ...authHeader() } })
            .then(async (r) => {
                if (!r.ok) {
                    const data = await r.json().catch(() => ({}));
                    throw new Error(data.error || `HTTP ${r.status}`);
                }
                return r.json();
            })
            .then(setLives)
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    const loadMeta = useCallback(() => {
        apiFetch("/api/live-meta")
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => {
                const map = {};
                (data || []).forEach((m) => {
                    map[m.codex] = m;
                });
                setMeta(map);
            })
            .catch((err) => setError(err.message))
            .finally(() => setMetaLoaded(true));
    }, [apiFetch]);

    useEffect(() => {
        loadMeta();
    }, [loadMeta]);

    const archiveLive = async (codex) => {
        try {
            await apiFetch(`/api/live-meta/${encodeURIComponent(codex)}/archive`, { method: "POST" });
            loadMeta();
        } catch (err) {
            setError(err.message);
        }
    };

    const unarchiveLive = async (codex) => {
        try {
            await apiFetch(`/api/live-meta/${encodeURIComponent(codex)}/unarchive`, { method: "POST" });
            loadMeta();
        } catch (err) {
            setError(err.message);
        }
    };

    const deleteLive = async (codex) => {
        if (
            !window.confirm(
                `Supprimer définitivement le live "${codex}" ?\n\nCeci efface réellement le snapshot ` +
                `(live_snapshots) — irréversible. La page live publique et les documents FIS liés au codex ` +
                `ne seront plus servis.`
            )
        ) {
            return;
        }
        try {
            const res = await apiFetch(`/api/live/${encodeURIComponent(codex)}`, { method: "DELETE" });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }
            setLives((prev) => prev.filter((l) => l.codex !== codex));
        } catch (err) {
            setError(err.message);
        }
    };

    const formatDate = (live) => {
        // La date de la course (TPARAMETERS.DATE, serial Excel) prime ;
        // à défaut on retombe sur la date de dernière mise à jour du live.
        if (live.date) return excelSerialToDateString(live.date);
        if (live.updatedAt) return new Date(live.updatedAt).toLocaleDateString("fr-CH");
        return "—";
    };

    const visibleLives = useMemo(
        () => lives.filter((l) => showArchived || !meta[l.codex]?.archivedAt),
        [lives, meta, showArchived]
    );
    const archivedCount = useMemo(() => lives.filter((l) => meta[l.codex]?.archivedAt).length, [lives, meta]);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h4">Vue d'ensemble des lives</Typography>
                {archivedCount > 0 && (
                    <Button size="small" onClick={() => setShowArchived((s) => !s)}>
                        {showArchived ? "Masquer" : "Afficher"} les archivés ({archivedCount})
                    </Button>
                )}
            </Box>

            {(loading || !metaLoaded) && <CircularProgress />}

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    Erreur de chargement : {error}
                </Typography>
            )}

            {!loading && metaLoaded && !error && visibleLives.length === 0 && (
                <Typography>Aucun live généré pour l'instant.</Typography>
            )}

            {!loading && metaLoaded && !error && visibleLives.length > 0 && (
                <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
                    <Table size="small" sx={{ minWidth: 1700 }}>
                        <TableHead>
                            <TableRow sx={{ "& th": { fontWeight: "bold", whiteSpace: "nowrap" } }}>
                                <TableCell>Archiver</TableCell>
                                <TableCell>Supprimer</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Codex</TableCell>
                                <TableCell>Lien live</TableCell>
                                <TableCell>Lien live/config</TableCell>
                                <TableCell>Codex</TableCell>
                                <TableCell>Codex_participants</TableCell>
                                <TableCell>Codex_SL1</TableCell>
                                <TableCell>Codex_IORes1</TableCell>
                                <TableCell>Codex_ORes1</TableCell>
                                <TableCell>Codex_SL2</TableCell>
                                <TableCell>Codex_IORes2</TableCell>
                                <TableCell>Codex_ORes2</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleLives.map((live) => {
                                const isArchived = !!meta[live.codex]?.archivedAt;
                                return (
                                    <TableRow key={live.codex} hover sx={{ opacity: isArchived ? 0.5 : 1 }}>
                                        <TableCell>
                                            {isArchived ? (
                                                <Tooltip title="Désarchiver">
                                                    <IconButton size="small" onClick={() => unarchiveLive(live.codex)}>
                                                        <UnarchiveIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="Archiver (masque de cette liste, réversible — ne supprime rien)">
                                                    <IconButton size="small" onClick={() => archiveLive(live.codex)}>
                                                        <ArchiveIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title="Supprimer définitivement (irréversible)">
                                                <IconButton size="small" color="error" onClick={() => deleteLive(live.codex)}>
                                                    <DeleteForeverIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(live)}</TableCell>
                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                            <strong>{live.codex}</strong>
                                            {live.title && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {live.title}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                component={Link}
                                                to={`/live/${live.codex}`}
                                                target="_blank"
                                                size="small"
                                                endIcon={<OpenInNewIcon fontSize="small" />}
                                            >
                                                Live
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                component={Link}
                                                to={`/live/${live.codex}/config`}
                                                target="_blank"
                                                size="small"
                                                endIcon={<OpenInNewIcon fontSize="small" />}
                                            >
                                                Config
                                            </Button>
                                        </TableCell>
                                        {DOC_SUFFIXES.map((suffix) => (
                                            <TableCell key={suffix}>
                                                <CopyField value={`${live.codex}${suffix}`} />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}